import { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Modal, Button, Spinner, Alert, Form, Row, Col } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import api from "../../services/api";
import moment from "moment-timezone";
import FormularioEventoModal from "./FormularioEventoModal";
import eventosForm, { EVENTOS } from "../../services/eventosForm";
import { getConfig } from "../../common/config";
import { getCurrentUser, isResultadoExameAposConfirmacao } from "../../common/utils";
import { Trans, useTranslation } from "react-i18next";
import { formatarMoeda, formatarNumero } from "../../i18n/format";

//Mesma regra de control-show (allow/deny-profiles) usada nos campos do ExamesComponent
const perfilPodeVerCampo = (campo) => {
    const controlShow = campo?.["control-show"];
    if (!controlShow) return true;

    const usuario = getCurrentUser();
    const tipoUsuario = usuario?.tipo_usuario || "";

    if (Array.isArray(controlShow["allow-profiles"])) {
        return controlShow["allow-profiles"].includes(tipoUsuario);
    }

    if (Array.isArray(controlShow["deny-profiles"])) {
        return !(controlShow["deny-profiles"].includes(tipoUsuario) ||
            (tipoUsuario === "prestador" && controlShow["deny-profiles"].includes(usuario?.prestador_categoria)));
    }

    return true;
};

/**
 * ConfirmarRealizacaoModal - Modal reutilizável para confirmar realização de serviço/agendamento.
 * 
 * Suporta:
 * - Campo de data/hora de realização
 * - Campos de terapia (Lote, Validade, Dose) quando aplicacao_id está presente
 * - Campo de valor para serviços de logística
 * - Campos dinâmicos da configuração do serviço
 * - Anotações opcionais
 */
const ConfirmarRealizacaoModal = (props) => {
    const {
        show,
        onHide,
        agendamento = null,
        contextData = null,
        onSuccess,
        programa_config = {},
    } = props;
    const { t, i18n } = useTranslation(["modals", "common", "validation"]);
    const navigate = useNavigate();
    // Form state
    const [dataRealizacao, setDataRealizacao] = useState("");
    const [anotacoes, setAnotacoes] = useState("");
    const [atendimentoFrustrado, setAtendimentoFrustrado] = useState(false);
    const [observacaoFrustrado, setObservacaoFrustrado] = useState("");
    const [nomeProfissional, setNomeProfissional] = useState("");
    const [valorServico, setValorServico] = useState("");
    const [arquivoLaudo, setArquivoLaudo] = useState(null);
    
    // Therapy fields
    const [terapiaLote, setTerapiaLote] = useState("");
    const [terapiaValidade, setTerapiaValidade] = useState("");
    const [terapiaDose, setTerapiaDose] = useState("");
    
    // Dynamic fields
    const [camposDinamicosValores, setCamposDinamicosValores] = useState({});
    const [camposDinamicosArquivos, setCamposDinamicosArquivos] = useState({});
    
    // UI state
    const [isLoading, setIsLoading] = useState(false);
    const [generalError, setGeneralError] = useState(null);
    const [errors, setErrors] = useState({});

    // Refs for auto-focus
    const fieldRefs = useRef({});

    // Formulario Evento State
    const [showFormularioEvento, setShowFormularioEvento] = useState(false);
    const [formularioEvento, setFormularioEvento] = useState(null);
    const [dadosFormularioEvento, setDadosFormularioEvento] = useState(null);

    // Computed values
    const configuracoesServico = agendamento?.dados_servico_procedimento?.configuracoes;
    //campos negados ao perfil logado (control-show) nem entram na tela, validacao ou payload
    const camposDinamicos = (configuracoesServico?.modal_confirmacao?.campos || []).filter(perfilPodeVerCampo);
    const temCamposDinamicos = camposDinamicos.length > 0;

    //texto do campo dinamico no idioma de quem confirma; o valor gravado continua o original. No cadastro:
    //"traducoes": {"en-US": {"label": "...", "opcoes": {"Positivo": "Positive"}, "descricoes_opcoes": {...}}}
    const traducaoCampo = (campo) => campo?.traducoes?.[i18n.language] || {};
    const rotuloCampo = (campo) => traducaoCampo(campo).label || campo.label;
    const rotuloOpcao = (campo, opcao) => traducaoCampo(campo).opcoes?.[opcao] || opcao;
    const descricaoOpcao = (campo, opcao) => traducaoCampo(campo).descricoes_opcoes?.[opcao] || campo.descricoes_opcoes?.[opcao];
    const observacaoCampo = (campo) => traducaoCampo(campo).observacao || campo.observacao;
    const isLogista = agendamento?.dados_servico_procedimento?.servico_logistica === true;
    const permiteAlterarValor = !!agendamento?.dados_servico_procedimento?.permite_alterar_valor_agendamento;
    const ocultarSolicitacaoExames = !!agendamento?.dados_servico_procedimento?.ocultar_solicitacao_exames;
    const hasTerapiaVinculada = Boolean(agendamento?.aplicacao_id);
    // Terapia exigida somente quando há aplicacao_id E o programa não desabilitou a obrigatoriedade
    const deveExigirCamposTerapia = hasTerapiaVinculada &&
        getConfig(agendamento?.dados_programa?.configuracoes || programa_config, 'agendamentos.desabilitar_obrigatoriedade_terapia_vinculada', false) !== true;
    
    //Os slugs 'laudo' e 'arquivo' sao o proprio arquivo do exame (HNS-1718): com o laudo fixo
    //junto o usuario via "Laudo (Opcional)" e "Upload do arquivo *" pro mesmo anexo. Outros
    //campos de arquivo nao escondem o laudo fixo.
    const temCampoLaudoDinamico = camposDinamicos.some(c => c.tipo === 'file' && ['laudo', 'arquivo'].includes(c.slug));

    // Atendimento frustrado: o profissional compareceu mas o atendimento não aconteceu.
    // Não há o que anexar nesse caso, então os arquivos deixam de ser exigidos e some da tela;
    // em troca, a observação passa a ser obrigatória.
    const permiteAtendimentoFrustrado = !!agendamento?.dados_servico_procedimento?.permite_atendimento_frustrado;
    const percentualCobrancaFrustrado = agendamento?.dados_servico_procedimento?.percentual_cobranca_frustrado;
    const ocultarArquivos = permiteAtendimentoFrustrado && atendimentoFrustrado;

    useEffect(() => {
        if (show && agendamento) {
            const now = moment().tz("America/Sao_Paulo").format("YYYY-MM-DDTHH:mm");
            setDataRealizacao(now);
            setAnotacoes("");
            setAtendimentoFrustrado(false);
            setObservacaoFrustrado("");
            setNomeProfissional(
                contextData?.prestador_nome || 
                agendamento?.dados_prestador?.nome || 
                ""
            );

            setValorServico(agendamento?.valor || "");
            setTerapiaLote("");
            setTerapiaValidade("");
            setTerapiaDose("");
            setArquivoLaudo(null);
            setCamposDinamicosValores({});
            setCamposDinamicosArquivos({});
            setGeneralError(null);
            setErrors({});
        }
    }, [show, agendamento, contextData]);

    /**
     * O valor fica guardado cru (ponto decimal), seja no preenchimento inicial vindo do
     * agendamento, seja no que o usuario digita. Tratar isso como texto
     * mascarado - tirando os pontos como se fossem separador de milhar - fazia "320.00"
     * virar 32000, ou seja, o servico era faturado 100x maior sempre que o valor vinha
     * preenchido e o usuario nao mexia no campo.
     */
    const valorParaAPI = (valor) => {
        if (typeof valor === 'number') return valor;
        const bruto = String(valor ?? '').replace('R$ ', '').trim();
        if (!bruto) return NaN;
        //com virgula e mascara ("1.250,00"); sem virgula e numero cru ("1250.00")
        const limpo = bruto.includes(',') ? bruto.split('.').join('').replace(',', '.') : bruto;
        return parseFloat(limpo);
    };

    //digitacao da direita para a esquerda, como na edicao do agendamento: os dois ultimos digitos
    //sao centavos ("185000" -> 1.850,00). Antes os digitos eram reais e "185000" virava R$ 185.000,00
    const handleValorChange = (e) => {
        const digitos = String(e.target.value || '').replace(/\D/g, '');
        setValorServico(digitos ? (parseInt(digitos, 10) / 100).toFixed(2) : '');
        if (errors.valorServico) {
             setErrors(prev => ({ ...prev, valorServico: null }));
        }
    };

    // Dynamic field change handlers
    const handleDynamicFieldChange = useCallback((slug, value) => {
        setCamposDinamicosValores(prev => ({ ...prev, [slug]: value }));
        setErrors(prev => {
            if (prev[slug]) {
                const newErrors = { ...prev };
                delete newErrors[slug];
                return newErrors;
            }
            return prev;
        });
    }, []);

    const handleDynamicFileChange = useCallback((slug, file) => {
        setCamposDinamicosArquivos(prev => ({ ...prev, [slug]: file }));
        setErrors(prev => {
           if (prev[slug]) {
               const newErrors = { ...prev };
               delete newErrors[slug];
               return newErrors;
           }
           return prev;
       });
    }, []);

    /**
     * Avalia uma condição individual (usado por visivel_condicional e obrigatorio_condicional)
     */
    const avaliarCondicaoUnica = useCallback((condicao) => {
        if (condicao.preenchido !== undefined) {
            const temValor = Boolean(camposDinamicosValores[condicao.campo]?.trim());
            const temArquivo = Boolean(camposDinamicosArquivos[condicao.campo]);
            const estaPreenchido = temValor || temArquivo;
            return condicao.preenchido === estaPreenchido;
        }

        if (condicao.valores) {
            const valorAtual = camposDinamicosValores[condicao.campo];
            return condicao.valores.includes(valorAtual);
        }

        return true;
    }, [camposDinamicosValores, camposDinamicosArquivos]);

    /**
     * Determina se um campo deve ser visível baseado em visivel_condicional
     */
    const isCampoVisivel = useCallback((campo) => {
        //no atendimento frustrado não existe arquivo para anexar
        if (ocultarArquivos && campo.tipo === "file") return false;

        if (!campo.visivel_condicional) return true;

        const condicao = campo.visivel_condicional;

        if (condicao.todas) {
            return condicao.todas.every(cond => avaliarCondicaoUnica(cond));
        }

        return avaliarCondicaoUnica(condicao);
    }, [avaliarCondicaoUnica, ocultarArquivos]);

    /**
     * Determina se um campo é obrigatório (considerando obrigatorio_condicional)
     */
    const isCampoObrigatorio = useCallback((campo) => {
        let isObrigatorio = campo.obrigatorio === true;

        if (campo.obrigatorio_condicional) {
            const { campo: campoRef, valores } = campo.obrigatorio_condicional;
            const valorAtual = camposDinamicosValores[campoRef];
            isObrigatorio = valores.includes(valorAtual);
        }

        return isObrigatorio;
    }, [camposDinamicosValores]);

    const configExames = ((props.programa_config) ? props.programa_config['Exames_Component'] : {}) || {};
    const exigeLaudo = configExames['exige_laudo_exame']?.includes('obrigatorio');
    const ocultarCampoLaudo = configExames['ocultar_laudo_confirmacao'] === true;
    const mostrarCampoLaudo = !ocultarCampoLaudo;
 
    
    
    // Validation
    const validate = () => {
        const newErrors = {};

        if (!dataRealizacao) {
            newErrors.dataRealizacao = t("modals:confirmCompletion.dateRequired");
        }

        if (isLogista || permiteAlterarValor) {
            if (!String(valorServico).trim()) {
                newErrors.valorServico = t("modals:confirmCompletion.valueRequired");
            } else {
                 const num = valorParaAPI(valorServico);
                 if (Number.isNaN(num) || num <= 0) {
                     newErrors.valorServico = t("modals:confirmCompletion.valuePositive");
                 }
            }
        }

        if (deveExigirCamposTerapia) {
            if (!terapiaLote.trim()) newErrors.terapiaLote = t("modals:confirmCompletion.fieldRequired");
            if (!terapiaValidade.trim()) newErrors.terapiaValidade = t("modals:confirmCompletion.fieldRequired");
            if (!terapiaDose.trim()) newErrors.terapiaDose = t("modals:confirmCompletion.fieldRequired");
        }

        if (exigeLaudo && !arquivoLaudo && !ocultarSolicitacaoExames && !temCampoLaudoDinamico && !ocultarArquivos) {
            newErrors.arquivoLaudo = t("modals:confirmCompletion.reportRequired");
        }

        if (ocultarArquivos && !observacaoFrustrado.trim()) {
            newErrors.observacaoFrustrado = t("common:frustratedEncounter.reasonRequired");
        }

        // Validate dynamic fields
        camposDinamicos.forEach(campo => {
            if (!isCampoVisivel(campo)) return;
            if (!isCampoObrigatorio(campo)) return;

            let isEmpty = false;
            let msg = t("validation:dynamicField.required", { campo: rotuloCampo(campo) });

            if (campo.tipo === "file") {
                if (!camposDinamicosArquivos[campo.slug]) isEmpty = true;
            } else if (campo.tipo === "radio") {
                if (!camposDinamicosValores[campo.slug]) isEmpty = true;
            } else {
                if (!camposDinamicosValores[campo.slug]?.trim()) isEmpty = true;
            }

            if (isEmpty) {
                newErrors[campo.slug] = msg;
            }
        });

        return newErrors;
    };

    /**
     * Campo dinamico pode declarar `acao_reporte_farmaco` com o valor que exige um reporte
     * de evento adverso (ex.: "Sim" em "contem eventos adversos?"). A resposta sozinha nao
     * abre reporte nenhum - por isso avisamos e levamos a pessoa ate o formulario.
     */
    const exigeReporteFarmaco = () => camposDinamicos.find(c =>
        c.acao_reporte_farmaco && camposDinamicosValores[c.slug] === c.acao_reporte_farmaco
    );

    const direcionarReporteFarmaco = async () => {
        const campo = exigeReporteFarmaco();
        if (!campo) return;

        await Swal.fire({
            title: t("modals:confirmCompletion.adverseEventTitle"),
            html: t("modals:confirmCompletion.adverseEventHtml", { resposta: campo.acao_reporte_farmaco, campo: campo.label }),
            icon: "warning",
            confirmButtonText: t("modals:confirmCompletion.adverseEventConfirm"),
            confirmButtonColor: "#3cc172",
            allowOutsideClick: false,
        });

        //leva o contexto para o formulario ja abrir com o programa e o paciente do
        //agendamento - quem acabou de confirmar nao deveria ter que reencontra-los
        navigate("/reportes-farmaco/novo", {
            state: {
                origem: {
                    programa_id: agendamento?.dados_programa?.id || agendamento?.programa_id,
                    atendimento_id: contextData?.atendimento_id || agendamento?.atendimento_id,
                    beneficiario_id: contextData?.beneficiario_id || agendamento?.beneficiario_id,
                    paciente_nome: agendamento?.dados_beneficiario?.nome,
                },
            },
        });
    };

    const enviarConfirmacao = async (dadosEvento = null) => {


        setIsLoading(true);
        setGeneralError(null);
        setErrors({});

        try {
            const dataRealizacaoFormatted = moment(dataRealizacao)
                .tz("America/Sao_Paulo")
                .format("YYYY-MM-DD HH:mm:ss");

            //So o arquivo do campo fixo de laudo sobe por /agendamentos/arquivos/. O de campo
            //dinamico ja vai no proprio PUT como arquivo_<slug>, que grava com o nome real e
            //o mesmo status de aprovacao - reenviar aqui gravava o mesmo arquivo duas vezes,
            //a segunda com o nome generico "Laudo - <servico>".
            //Atendimento frustrado (HNS-1710) nao envia arquivo nenhum.
            const fileToUpload = ocultarArquivos ? null : arquivoLaudo;

            const situacaoFinal = "finalizado";

            const payload = {
                //: agendamento.data_agendamento,
                data_realizacao: dataRealizacaoFormatted,
                adicionar_servico_atendimento: true,
                servico_procedimento_id: agendamento.servico_procedimento_id,
                situacao: situacaoFinal,
            };

            if (contextData?.beneficiario_id) {
                payload.beneficiario_id = contextData.beneficiario_id;
            }
            if (contextData?.atendimento_id) {
                payload.atendimento_id = contextData.atendimento_id;
            }
            if (agendamento.prestador_id) {
                payload.prestador_id = agendamento.prestador_id;
            }
            if (agendamento.tipo) {
                payload.tipo = agendamento.tipo;
            }

            if (anotacoes.trim()) {
                payload.anotacoes = anotacoes;
            }

            if (permiteAtendimentoFrustrado) {
                payload.atendimento_frustrado = atendimentoFrustrado;

                if (atendimentoFrustrado) {
                    payload.observacao_frustrado = observacaoFrustrado;
                }
            }

            if (nomeProfissional.trim()) {
                payload.nome_profissional = nomeProfissional;
            }

            if (isLogista || permiteAlterarValor) {
                payload.valor = valorParaAPI(valorServico);
            }

            if (deveExigirCamposTerapia) {
                payload.aplicacao_lote = terapiaLote;
                payload.aplicacao_validade = terapiaValidade;
                payload.aplicacao_dose = terapiaDose;
            }

            // Dynamic fields
            if (temCamposDinamicos && Object.keys(camposDinamicosValores).length > 0) {
                const dadosConfirmacaoArray = [];
                
                camposDinamicos.forEach(campo => {
                    const valor = camposDinamicosValores[campo.slug];
                    if (valor !== undefined && valor !== null && valor !== "") {
                        dadosConfirmacaoArray.push({
                            slug: campo.slug,
                            label: campo.label,
                            value: valor,
                            tipo: campo.tipo
                        });
                    }
                });

                if (dadosConfirmacaoArray.length > 0) {
                    payload.dados_confirmacao = dadosConfirmacaoArray;
                }
            }

            if (dadosEvento) {
                payload.dados_formulario_aprovacao = dadosEvento;
            }

            //arquivos escolhidos antes de marcar o atendimento como frustrado não devem subir
            const arquivosDinamicos = ocultarArquivos ? {} : camposDinamicosArquivos;
            const hasFiles = Object.keys(arquivosDinamicos).length > 0;
            let requestPromise;

            if (hasFiles) {
                const formData = new FormData();
                Object.entries(payload).forEach(([key, value]) => {
                    if (value !== null && value !== undefined) {
                        if (typeof value === "object" && !(value instanceof File)) {
                            formData.append(key, JSON.stringify(value));
                        } else {
                            formData.append(key, value);
                        }
                    }
                });

                Object.entries(arquivosDinamicos).forEach(([slug, arquivo]) => {
                    formData.append(`arquivo_${slug}`, arquivo);
                });


                requestPromise = api.put(`/agendamentos/${agendamento.id}/`, formData, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
            } else {
                requestPromise = api.put(`/agendamentos/${agendamento.id}/`, payload);
            }

            await requestPromise;

            // Update kit if linked
            if (agendamento.atendimento_kit_id) {
                const dataEnvioKit = moment(dataRealizacao).tz("America/Sao_Paulo").format("YYYY-MM-DD");
                await api.put(`/atendimentos/kits/${agendamento.atendimento_kit_id}/`, {
                    data_envio_realizado: dataEnvioKit,
                    status: "enviado",
                });
            }

            // Create relato
            const etapaId = contextData?.etapa_atual?.id || agendamento.etapa_atual?.id;
            const atendimentoId = contextData?.atendimento_id || agendamento.atendimento_id;
            
            if (etapaId && atendimentoId) {
                const relatoFormData = new FormData();
                relatoFormData.append("data", dataRealizacaoFormatted);
                relatoFormData.append("etapa_id", etapaId);
                relatoFormData.append("atendimento_id", atendimentoId);
                relatoFormData.append("tipo", "etapa");
                relatoFormData.append("titulo", `Realização de ${agendamento.dados_servico_procedimento?.nome}`);
                relatoFormData.append("servico_procedimento_id", agendamento.servico_procedimento_id);
                relatoFormData.append("ignorar_cadastro_servico", true);
                relatoFormData.append("agendamento_id", agendamento.id);

                await api.post("/atendimentos/relatos/", relatoFormData);
            }



            if (fileToUpload) {
                const formDataArquivo = new FormData();
                const agora = moment().tz("America/Sao_Paulo").format("YYYY-MM-DD HH:mm:ss");

                const configPrograma = agendamento?.dados_programa?.configuracoes || programa_config || {};
                const aprovaAutomatico = configPrograma.aprova_laudo_automatico === true;
                const statusAprovacao = aprovaAutomatico ? 'true' : '';

                formDataArquivo.append('arquivo', fileToUpload);
                formDataArquivo.append('nome', `Laudo - ${agendamento.dados_servico_procedimento?.nome || 'Serviço'}`);
                formDataArquivo.append('agendamento_id', agendamento.id);
                formDataArquivo.append('data', agora);
                formDataArquivo.append('tipo', 'laudo');
                formDataArquivo.append('pessoa_id', 1);
                formDataArquivo.append('aprovado', statusAprovacao);

                if (dadosEvento) {
                    formDataArquivo.append('dados_formulario_aprovacao', JSON.stringify(dadosEvento));
                }

                await api.post('/agendamentos/arquivos/', formDataArquivo, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
            }

            //A acao de reavaliacao (servicos com acao_reavaliacao na config) e criada pelo
            //backend na transicao para 'finalizado' - regra de negocio nao depende da tela.

            //onSuccess recarrega a pagina nas telas que usam este modal, entao o direcionamento
            //precisa acontecer antes - e no lugar dele, ja que vamos sair da tela.
            if (exigeReporteFarmaco()) {
                onHide();
                await direcionarReporteFarmaco();
                return;
            }

            onSuccess();
            onHide();
        } catch (err) {

            console.error("Erro ao confirmar serviço:", err);
            const errorMessage =
                err.response?.data?.message ||
                err.response?.data?.detail ||
                t("modals:confirmCompletion.errorGeneric");
            setGeneralError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async () => {
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            
            // Auto focus first error
            const firstKey = Object.keys(validationErrors)[0];
            const el = fieldRefs.current[firstKey];
            if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                el.focus();
            }
            return;
        }

        const configPrograma = agendamento?.dados_programa?.configuracoes || programa_config || {};
        const aprovaAutomatico = configPrograma.aprova_laudo_automatico === true;
        //quem informa o resultado depois nao tem o que responder agora: o laudo anexado aqui
        //e so o aviso de que o resultado saiu (HNS-1610)
        const resultadoDepois = isResultadoExameAposConfirmacao(configPrograma);

        if (arquivoLaudo && aprovaAutomatico && !resultadoDepois && !dadosFormularioEvento) {
            setIsLoading(true);
            try {
                const programaId = agendamento.dados_programa?.id || agendamento.programa_id;
                // Busca formulário ON_APPROVE_LAUDO para este programa
                const form = await eventosForm.buscarFormularioEvento(EVENTOS.ON_APPROVE_LAUDO, programaId);
                
                if (form) {
                    setFormularioEvento(form);
                    setShowFormularioEvento(true);
                    setIsLoading(false);
                    return;
                }
            } catch (e) {
                console.error('Erro ao verificar formulário:', e);

            } finally {
                setIsLoading(false);
            }
        }

        enviarConfirmacao(dadosFormularioEvento);
    };

    const handleFormularioEventoSuccess = (respostas) => {
        setDadosFormularioEvento(respostas);
        setShowFormularioEvento(false);
        enviarConfirmacao(respostas);
    };

    // Render dynamic fields
    const renderDynamicFields = () => {
        if (!temCamposDinamicos) return null;

        // Filtrar apenas campos visíveis
        const camposVisiveis = camposDinamicos.filter(campo => isCampoVisivel(campo));

        if (camposVisiveis.length === 0) return null;

        return (
            <div className="dynamic-fields-section mt-3 pt-3 border-top">
                {camposVisiveis.map((campo) => {
                    const isRequired = isCampoObrigatorio(campo);
                    const requiredMark = isRequired ? <span className="text-danger">*</span> : null;

                    switch (campo.tipo) {
                        case "file":
                            return (
                                <Form.Group className="mb-3" key={campo.slug}>
                                    <Form.Label>{rotuloCampo(campo)} {requiredMark}</Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept={campo.extensoes || ".pdf,.jpg,.jpeg,.png"}
                                        onChange={(e) => handleDynamicFileChange(campo.slug, e.target.files[0])}
                                        isInvalid={!!errors[campo.slug]}
                                        ref={el => fieldRefs.current[campo.slug] = el}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors[campo.slug]}
                                    </Form.Control.Feedback>
                                    {campo.observacao && (
                                        <Form.Text className="text-muted">{observacaoCampo(campo)}</Form.Text>
                                    )}
                                </Form.Group>
                            );

                        case "radio":
                            return (
                                <Form.Group className="mb-3" key={campo.slug}>
                                    <Form.Label>{rotuloCampo(campo)} {requiredMark}</Form.Label>
                                    <div>
                                        {(campo.opcoes || []).map((opcao, idx) => {
                                            const descricao = descricaoOpcao(campo, opcao);
                                            return (
                                                <div key={idx}>
                                                    <Form.Check
                                                        type="radio"
                                                        id={`${campo.slug}-${idx}`}
                                                        name={campo.slug}
                                                        label={rotuloOpcao(campo, opcao)}
                                                        value={opcao}
                                                        checked={camposDinamicosValores[campo.slug] === opcao}
                                                        onChange={(e) => handleDynamicFieldChange(campo.slug, e.target.value)}
                                                        isInvalid={!!errors[campo.slug]}
                                                        ref={idx === 0 ? (el => fieldRefs.current[campo.slug] = el) : null}
                                                    />
                                                    {descricao && (
                                                        <Form.Text className="text-muted d-block ms-4 mb-2">
                                                            {descricao}
                                                        </Form.Text>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {errors[campo.slug] && (
                                        <div className="text-danger small mt-1">
                                            {errors[campo.slug]}
                                        </div>
                                    )}
                                    {campo.observacao && (
                                        <Form.Text className="text-muted">{observacaoCampo(campo)}</Form.Text>
                                    )}
                                </Form.Group>
                            );

                        case "textarea":
                            return (
                                <Form.Group className="mb-3" key={campo.slug}>
                                    <Form.Label>{rotuloCampo(campo)} {requiredMark}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder={campo.placeholder || ""}
                                        value={camposDinamicosValores[campo.slug] || ""}
                                        onChange={(e) => handleDynamicFieldChange(campo.slug, e.target.value)}
                                        isInvalid={!!errors[campo.slug]}
                                        ref={el => fieldRefs.current[campo.slug] = el}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors[campo.slug]}
                                    </Form.Control.Feedback>
                                    {campo.observacao && (
                                        <Form.Text className="text-muted">{observacaoCampo(campo)}</Form.Text>
                                    )}
                                </Form.Group>
                            );

                        case "text":
                        default:
                            return (
                                <Form.Group className="mb-3" key={campo.slug}>
                                    <Form.Label>{rotuloCampo(campo)} {requiredMark}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder={campo.placeholder || ""}
                                        value={camposDinamicosValores[campo.slug] || ""}
                                        onChange={(e) => handleDynamicFieldChange(campo.slug, e.target.value)}
                                        isInvalid={!!errors[campo.slug]}
                                        ref={el => fieldRefs.current[campo.slug] = el}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors[campo.slug]}
                                    </Form.Control.Feedback>
                                    {campo.observacao && (
                                        <Form.Text className="text-muted">{observacaoCampo(campo)}</Form.Text>
                                    )}
                                </Form.Group>
                            );
                    }
                })}
            </div>
        );
    };

    const minDate = moment().subtract(30, "days").format("YYYY-MM-DDTHH:mm");
    const maxDate = moment().format("YYYY-MM-DDTHH:mm");

    return (
        <>
        <Modal show={show} onHide={onHide} size="lg" centered backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>{t("modals:confirmCompletion.title")}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
                {generalError && (
                    <Alert variant="danger" onClose={() => setGeneralError(null)} dismissible>
                        {generalError}
                    </Alert>
                )}

                <p>
                    <Trans
                        i18nKey="modals:confirmCompletion.question"
                        values={{ servico: agendamento?.dados_servico_procedimento?.nome }}
                        components={{ b: <strong /> }}
                    />
                </p>

                {/* Data de Realização */}
                <Form.Group className="mb-3">
                    <Form.Label>
                        {t("modals:confirmCompletion.dateLabel")} <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                        type="datetime-local"
                        value={dataRealizacao}
                        min={minDate}
                        max={maxDate}
                        onChange={(e) => {
                            setDataRealizacao(e.target.value);
                            if (errors.dataRealizacao) setErrors(prev => ({ ...prev, dataRealizacao: null }));
                        }}
                        isInvalid={!!errors.dataRealizacao}
                        ref={el => fieldRefs.current['dataRealizacao'] = el}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.dataRealizacao}
                    </Form.Control.Feedback>
                </Form.Group>

                {/* Logistics or Permitted Value */}
                {(isLogista || permiteAlterarValor) && (
                    <Form.Group className="mb-3">
                        <Form.Label>
                            {t("modals:confirmCompletion.valueLabel")} <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            inputMode="numeric"
                            placeholder={formatarMoeda(0)}
                            value={String(valorServico).trim() === '' ? '' : formatarMoeda(Number(valorServico))}
                            onChange={handleValorChange}
                            isInvalid={!!errors.valorServico}
                            ref={el => fieldRefs.current['valorServico'] = el}
                        />
                        <Form.Control.Feedback type="invalid">
                            {errors.valorServico}
                        </Form.Control.Feedback>
                    </Form.Group>
                )}

                {/* Therapy Fields */}
                {deveExigirCamposTerapia && (
                    <div className="therapy-section mt-3 pt-3 border-top">
                        <p className="mb-3 fw-bold">{t("modals:confirmCompletion.therapyTitle")}</p>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {t("modals:confirmCompletion.lot")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder={t("modals:confirmCompletion.lot")}
                                        value={terapiaLote}
                                        onChange={(e) => {
                                            setTerapiaLote(e.target.value);
                                            if (errors.terapiaLote) setErrors(prev => ({ ...prev, terapiaLote: null }));
                                        }}
                                        isInvalid={!!errors.terapiaLote}
                                        ref={el => fieldRefs.current['terapiaLote'] = el}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.terapiaLote}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {t("modals:confirmCompletion.expiry")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="date"
                                        value={terapiaValidade}
                                        onChange={(e) => {
                                            setTerapiaValidade(e.target.value);
                                            if (errors.terapiaValidade) setErrors(prev => ({ ...prev, terapiaValidade: null }));
                                        }}
                                        isInvalid={!!errors.terapiaValidade}
                                        ref={el => fieldRefs.current['terapiaValidade'] = el}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.terapiaValidade}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {t("modals:confirmCompletion.dose")} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder={t("modals:confirmCompletion.dosePlaceholder")}
                                        value={terapiaDose}
                                        onChange={(e) => {
                                            setTerapiaDose(e.target.value);
                                            if (errors.terapiaDose) setErrors(prev => ({ ...prev, terapiaDose: null }));
                                        }}
                                        isInvalid={!!errors.terapiaDose}
                                        ref={el => fieldRefs.current['terapiaDose'] = el}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.terapiaDose}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    </div>
                )}

                {/* Atendimento Frustrado */}
                {permiteAtendimentoFrustrado && (
                    <div className="mb-3 pt-3 border-top">
                        <Form.Check
                            type="checkbox"
                            id="atendimento-frustrado"
                            label={t("common:frustratedEncounter.label")}
                            checked={atendimentoFrustrado}
                            onChange={(e) => {
                                setAtendimentoFrustrado(e.target.checked);
                                if (!e.target.checked) setObservacaoFrustrado("");
                                setErrors(prev => ({ ...prev, observacaoFrustrado: null }));
                            }}
                        />
                        <Form.Text className="text-muted d-block ms-4">
                            {t("common:frustratedEncounter.hint")}
                            {percentualCobrancaFrustrado !== null && percentualCobrancaFrustrado !== undefined && (
                                <> {t("common:frustratedEncounter.chargeNotice", { percentual: formatarNumero(percentualCobrancaFrustrado, { maximumFractionDigits: 2 }) })}</>
                            )}
                        </Form.Text>

                        {atendimentoFrustrado && (
                            <Form.Group className="mt-3">
                                <Form.Label>
                                    {t("common:frustratedEncounter.observationLabel")} <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    placeholder={t("common:frustratedEncounter.observationPlaceholder")}
                                    value={observacaoFrustrado}
                                    onChange={(e) => {
                                        setObservacaoFrustrado(e.target.value);
                                        if (errors.observacaoFrustrado) setErrors(prev => ({ ...prev, observacaoFrustrado: null }));
                                    }}
                                    isInvalid={!!errors.observacaoFrustrado}
                                    ref={el => fieldRefs.current['observacaoFrustrado'] = el}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {errors.observacaoFrustrado}
                                </Form.Control.Feedback>
                            </Form.Group>
                        )}
                    </div>
                )}

                {/* Laudo do Exame */}
                {(mostrarCampoLaudo && !ocultarSolicitacaoExames && !temCampoLaudoDinamico && !ocultarArquivos) && (
                    <Form.Group className="mb-3">
                        <Form.Label>
                            {t("modals:confirmCompletion.reportLabel")} {exigeLaudo ? <span className="text-danger">*</span> : t("modals:confirmCompletion.optional")}
                        </Form.Label>
                        <Form.Control
                            type="file"
                            accept=".pdf, .jpg, .jpeg, .png"
                            onChange={(e) => {
                                setArquivoLaudo(e.target.files[0] || null);
                                if (errors.arquivoLaudo) setErrors(prev => ({ ...prev, arquivoLaudo: null }));
                            }}
                            isInvalid={!!errors.arquivoLaudo}
                            ref={el => fieldRefs.current['arquivoLaudo'] = el}
                        />
                         <Form.Control.Feedback type="invalid">
                            {errors.arquivoLaudo}
                        </Form.Control.Feedback>
                    </Form.Group>
                )}

                {/* Dynamic Fields */}
                {renderDynamicFields()}

                {/* Anotações */}
                <Form.Group className="mb-3">
                    <Form.Label>{t("modals:confirmCompletion.notesLabel")}</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        value={anotacoes}
                        onChange={(e) => setAnotacoes(e.target.value)}
                    />
                </Form.Group>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={isLoading}>
                    {t("common:button.cancel")}
                </Button>
                <Button variant="success" onClick={handleSubmit} disabled={isLoading}>
                    {isLoading ? (
                        <>
                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                            {t("modals:confirmCompletion.confirming")}
                        </>
                    ) : (
                        t("modals:confirmCompletion.title")
                    )}
                </Button>
            </Modal.Footer>
        </Modal>

        {/* Modal de Formulário de Evento (para aprovação automática) */}
        {showFormularioEvento && (
            <FormularioEventoModal
                show={showFormularioEvento}
                onHide={() => {
                    setShowFormularioEvento(false);
                    setIsLoading(false);
                }}
                formulario={formularioEvento}
                onSuccess={handleFormularioEventoSuccess}
                loading={isLoading}
            />
        )}
        </>
    );
};

ConfirmarRealizacaoModal.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    agendamento: PropTypes.shape({
        id: PropTypes.number,
        data_agendamento: PropTypes.string,
        servico_procedimento_id: PropTypes.number,
        prestador_id: PropTypes.number,
        tipo: PropTypes.string,
        aplicacao_id: PropTypes.number,
        atendimento_kit_id: PropTypes.number,
        atendimento_id: PropTypes.number,
        dados_servico_procedimento: PropTypes.shape({
            nome: PropTypes.string,
            servico_logistica: PropTypes.bool,
            configuracoes: PropTypes.object,
            permite_atendimento_frustrado: PropTypes.bool,
            percentual_cobranca_frustrado: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        }),
        dados_prestador: PropTypes.shape({
            nome: PropTypes.string,
        }),
        etapa_atual: PropTypes.shape({
            id: PropTypes.number,
        }),
    }),
    contextData: PropTypes.shape({
        beneficiario_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        atendimento_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        etapa_atual: PropTypes.shape({
            id: PropTypes.number,
        }),
        prestador_nome: PropTypes.string,
    }),
    programa_config: PropTypes.object,
    onSuccess: PropTypes.func.isRequired,
};

export default ConfirmarRealizacaoModal;
